import { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { gsap } from 'gsap';

import './BubbleMenu.css';

const DEFAULT_ITEMS = [
  {
    label: 'home',
    href: '#',
    ariaLabel: 'Home',
    rotation: -8,
    hoverStyles: { bgColor: '#3b82f6', textColor: '#ffffff' }
  },
  {
    label: 'about',
    href: '#',
    ariaLabel: 'About',
    rotation: 8,
    hoverStyles: { bgColor: '#10b981', textColor: '#ffffff' }
  },
  {
    label: 'projects',
    href: '#',
    ariaLabel: 'Documentation',
    rotation: 8,
    hoverStyles: { bgColor: '#f59e0b', textColor: '#ffffff' }
  },
  {
    label: 'articles',
    href: '#',
    ariaLabel: 'Articles',
    rotation: 8,
    hoverStyles: { bgColor: '#ef4444', textColor: '#ffffff' }
  },
  {
    label: 'contact',
    href: '#',
    ariaLabel: 'Contact',
    rotation: -8,
    hoverStyles: { bgColor: '#8b5cf6', textColor: '#ffffff' }
  }
];

function BubbleMenu({
  logo,
  logoHref = '/',
  onMenuClick,
  onLogoClick,
  onNavigate,
  className,
  style,
  menuAriaLabel = 'Toggle menu',
  menuBg = '#fff',
  menuContentColor = '#111',
  useFixedPosition = false,
  items,
  animationEase = 'expo.out',
  animationDuration = 0.35,
  staggerDelay = 0.08
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const overlayRef = useRef(null);
  const bubblesRef = useRef([]);
  const labelRefs = useRef([]);
  const toggleBubbleRef = useRef(null);

  const menuItems = items?.length ? items : DEFAULT_ITEMS;
  const overlayBg = menuBg === '#ffffff'
    ? 'var(--glass-overlay-light)'
    : 'var(--glass-overlay-dark)';
  const pillBg = menuBg === '#ffffff'
    ? 'var(--glass-pill-light)'
    : 'var(--glass-pill-dark)';
  const overlayStyle = useMemo(() => ({
    background: overlayBg,
    pointerEvents: 'auto',
    '--bubble-pill-bg': pillBg
  }), [overlayBg, pillBg]);
  const containerClassName = ['bubble-menu', useFixedPosition ? 'fixed' : 'absolute', className]
    .filter(Boolean)
    .join(' ');

  const getBubbleRotation = (bubble, item) => {
    if (typeof window === 'undefined') {
      return item?.rotation ?? 0;
    }

    if (window.innerWidth >= 900) {
      return item?.rotation ?? 0;
    }

    const mobileRotation = bubble?.parentElement
      ? getComputedStyle(bubble.parentElement).getPropertyValue('--mob-rot').trim()
      : '';
    const parsedRotation = Number.parseFloat(mobileRotation);

    return Number.isFinite(parsedRotation) ? parsedRotation : 0;
  };

  const animateOpen = () => {
    const overlay = overlayRef.current;
    const bubbles = bubblesRef.current.filter(Boolean);
    const labels = labelRefs.current.filter(Boolean);

    gsap.killTweensOf([overlay, ...bubbles, ...labels]);
    gsap.set(overlay, { display: 'flex' });
    gsap.to(overlay, { autoAlpha: 1, duration: 0.25, ease: 'power2.out' });

    bubbles.forEach((bubble, i) => {
      const item = menuItems[i];
      gsap.set(bubble, {
        rotation: getBubbleRotation(bubble, item),
        scale: 0,
        transformOrigin: '50% 50%'
      });
    });
    gsap.set(labels, { y: 24, autoAlpha: 0 });

    bubbles.forEach((bubble, i) => {
      const delay = i * staggerDelay + gsap.utils.random(-0.05, 0.05);
      const tl = gsap.timeline({ delay });
      tl.to(bubble, {
        rotation: getBubbleRotation(bubble, menuItems[i]),
        scale: 1,
        duration: animationDuration,
        ease: animationEase
      });
      if (labels[i]) {
        tl.to(labels[i], { y: 0, autoAlpha: 1, duration: animationDuration, ease: 'power3.out' }, `-=${animationDuration * 0.9}`);
      }
    });

    gsap.delayedCall(0.3, () => {
      bubblesRef.current.find(Boolean)?.focus();
    });
  };

  const animateClose = useCallback((callback) => {
    const overlay = overlayRef.current;
    const bubbles = bubblesRef.current.filter(Boolean);
    const labels = labelRefs.current.filter(Boolean);

    gsap.killTweensOf([overlay, ...bubbles, ...labels]);
    gsap.to(labels, { y: 24, autoAlpha: 0, duration: 0.15, ease: 'power3.in' });
    gsap.to(bubbles, { scale: 0, duration: 0.2, ease: 'power3.in' });
    gsap.to(overlay, {
      autoAlpha: 0,
      duration: 0.25,
      ease: 'power2.in',
      delay: 0.05,
      onComplete: () => {
        gsap.set(overlay, { display: 'none' });
        setIsMenuOpen(false);
        toggleBubbleRef.current?.focus();
        callback?.();
      }
    });
  }, []);

  const handleToggle = () => {
    if (isMenuOpen) {
      animateClose();
    } else {
      setIsMenuOpen(true);
      animateOpen();
    }
    onMenuClick?.(!isMenuOpen);
  };

  const handleLogoPress = () => {
    if (!onLogoClick) {
      return;
    }

    if (isMenuOpen) {
      animateClose(() => {
        onMenuClick?.(false);
        onLogoClick();
      });
      return;
    }

    onLogoClick();
  };

  const handleOverlayClick = useCallback(() => {
    animateClose();
  }, [animateClose]);

  const handleMenuListClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const menuItemElements = useMemo(() => menuItems.map((item, idx) => (
    <li key={item.href || item.label || idx} role="none" className="pill-col">
      <a
        role="menuitem"
        href={item.href}
        aria-label={item.ariaLabel || item.label}
        className="pill-link"
        style={{
          '--item-rot': `${item.rotation ?? 0}deg`,
          '--pill-color': menuContentColor,
          '--hover-bg': item.hoverStyles?.bgColor || '#f3f4f6',
          '--hover-color': item.hoverStyles?.textColor || menuContentColor
        }}
        ref={el => { bubblesRef.current[idx] = el; }}
        onClick={onNavigate ? (event) => {
          event.preventDefault();
          animateClose(() => onNavigate(item.href));
        } : undefined}
      >
        <span
          className="pill-label"
          ref={el => { labelRefs.current[idx] = el; }}
        >
          {item.label}
        </span>
      </a>
    </li>
  )), [animateClose, menuContentColor, menuItems, onNavigate]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const syncBubbleRotations = () => {
      bubblesRef.current.forEach((bubble, i) => {
        const item = menuItems[i];
        if (!bubble || !item) return;

        gsap.set(bubble, { rotation: getBubbleRotation(bubble, item) });
      });
    };

    const handleResize = () => {
      syncBubbleRotations();
    };

    syncBubbleRotations();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen, menuItems]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        animateClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = bubblesRef.current.filter(Boolean);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, animateClose]);

  return (
    <>
      <nav className={containerClassName} style={style} aria-label="Main navigation">
        <a
          href={logoHref}
          className="bubble logo-bubble"
          aria-label="Home"
          onClick={(e) => { if (onLogoClick) { e.preventDefault(); handleLogoPress(); } }}
          style={{ textDecoration: 'none' }}
        >
          <span className="logo-content">
            {typeof logo === 'string' ? <img src={logo} alt="Logo" className="bubble-logo" /> : logo}
          </span>
        </a>

        <button
          type="button"
          ref={toggleBubbleRef}
          className={`bubble toggle-bubble menu-btn ${isMenuOpen ? 'open' : ''}`}
          onClick={handleToggle}
          aria-label={menuAriaLabel}
          aria-expanded={isMenuOpen}
        >
          <span className="menu-line" style={{ background: menuContentColor }} />
          <span className="menu-line short" style={{ background: menuContentColor }} />
        </button>
      </nav>

      <div
        ref={overlayRef}
        className={`bubble-menu-items ${useFixedPosition ? 'fixed' : 'absolute'} ${isMenuOpen ? 'open' : ''}`}
        style={overlayStyle}
        aria-hidden={!isMenuOpen}
        onClick={handleOverlayClick}
      >
        <ul className={`pill-list items-${menuItems.length}`} role="menu" aria-label="Menu links" onClick={handleMenuListClick}>
          {menuItemElements}
        </ul>
      </div>
    </>
  );
}

export default memo(BubbleMenu);
